/* eslint-disable class-methods-use-this */
/* eslint-disable import/prefer-default-export */

import {
    DashboardClient, DownloadedFileResponse, ProblemDetails, RequestForQuoteDetails,
} from '../../api/web-api-client';
import { AccountDetails } from '../../authentication/accountContext';
import { setDashboardNotification } from '../../storage/notification';
import { NotificationSeverity } from '../../storage/types';
import { base64toBlob } from '../../utils';
import { DashBoardNotifications } from './dashboardNotifications';
import { Environment, QuoteStatus } from './enums';

/**
 * Gets downloadable files from CRM depending on status
 * @returns DownloadedFileResponse
 */
// eslint-disable-next-line max-len
export const getFileDetails = (accessToken: string, result: RequestForQuoteDetails, getFile: boolean, isQuotationTab?: boolean | undefined): Promise<DownloadedFileResponse | undefined> => {
    const dashboardClient = new DashboardClient();
    dashboardClient.setAuthToken(accessToken);

    switch (result.quoteRequestStatus) {
        case QuoteStatus.QuoteAccepted:
        case QuoteStatus.ArtifactReceived:
            return dashboardClient.getQuoteRequestAcceptedPDFByID(result.crmQuoteRequestId, getFile);
        case QuoteStatus.QuoteDeclined:
            return dashboardClient.getQuoteRequestRejectedPDFByID(result.crmQuoteRequestId, getFile);
        case QuoteStatus.QuoteAvailable:
        case QuoteStatus.QuoteClosed:
            return dashboardClient.getQuoteOfferPDFByQuoteID(result.crmQuoteId, getFile);
        case QuoteStatus.ReportIssued:
            return isQuotationTab
                ? dashboardClient.getQuoteRequestAcceptedPDFByID(result.crmQuoteRequestId, getFile)
                : dashboardClient.getQuoteReportPDFByID(result.crmQuoteRequestId, getFile);
        case QuoteStatus.ReportInProgress:
            return dashboardClient.getQuoteRequestAcceptedPDFByID(result.crmQuoteRequestId, getFile);
        default:
            // eslint-disable-next-line no-case-declarations
            const deferreds = [];
            return new Promise<undefined>((resolve, reject) => { deferreds.push({ resolve, reject }); });
    }
};

/**
 * Gets downloadable files from CRM depending on status
 * @returns DownloadedFileResponse
 */
// eslint-disable-next-line max-len
export const getQuotationFileDetails = (accessToken: string, result: RequestForQuoteDetails, getFile: boolean): Promise<DownloadedFileResponse | undefined> => {
    const dashboardClient = new DashboardClient();
    dashboardClient.setAuthToken(accessToken);

    switch (result.quoteRequestStatus) {
        case QuoteStatus.QuoteAccepted:
        case QuoteStatus.ArtifactReceived:
        case QuoteStatus.ReportIssued:
        case QuoteStatus.ReportInProgress:
            return dashboardClient.getQuoteRequestAcceptedPDFByID(result.crmQuoteRequestId, getFile);
        case QuoteStatus.QuoteDeclined:
            return dashboardClient.getQuoteRequestRejectedPDFByID(result.crmQuoteRequestId, getFile);
        case QuoteStatus.QuoteAvailable:
        case QuoteStatus.QuoteClosed:
            return dashboardClient.getQuoteOfferPDFByQuoteID(result.crmQuoteId, getFile);
        default:
            // eslint-disable-next-line no-case-declarations
            const deferreds = [];
            return new Promise<undefined>((resolve, reject) => { deferreds.push({ resolve, reject }); });
    }
};

export const getMakeModelDetails = (result: RequestForQuoteDetails): string => {
    switch (result.quoteRequestStatus) {
        case QuoteStatus.ArtifactReceived:
        case QuoteStatus.ReportInProgress:
            return result.portalArtefactName !== undefined ? result.portalArtefactName : '';
        default:
            return result.instrumentArtefactToBeCalibrated !== undefined ? result.instrumentArtefactToBeCalibrated : '';
    }
};

/**
 * Takes the filesizebytes value and converts into a formatted string
 * @param value The file size value received from the API
 * @returns a formatted string indicating the size of the file in either Mb or Kb
 */
export const getFileSize = (value: number): string => {
    const sizeInKb = value / 1024;
    return sizeInKb > 1024 ? `${(sizeInKb / 1024).toFixed(2)}Mb` : `${sizeInKb.toFixed(2)}Kb`;
};

/**
 * Creates a file url based off the fileData param
 * @param fileData The file represented as a base64 string
 * @returns returns a url that can be used to download or view the pdffile
 */
export const getFileUrlFromBase64 = (fileData: string): string => {
    const blob = base64toBlob(fileData, 'application/pdf');
    const fileUrl = URL.createObjectURL(blob);
    return fileUrl;
};

/**
 * Creates a file url based off the fileData param
 * @param fileData The file represented as a base64 string
 * @returns returns a url that can be used to download or view the pdffile
 */
export const getFileIdFromBase64 = (fileData: string): string => {
    const blob = base64toBlob(fileData, 'application/pdf');
    const fileUrl = URL.createObjectURL(blob);
    return fileUrl;
};

/**
 * Resolves the page number the pdf opens on
 * @param status the status of the quote
 * @returns a page number
 */
export const getQuoteOfferPageNumber = (status: string | undefined): string => {
    switch (status) {
        case QuoteStatus.QuoteDeclined:
            return '3';
        case QuoteStatus.QuoteAccepted:
        case QuoteStatus.ArtifactReceived:
        case QuoteStatus.ReportIssued:
        case QuoteStatus.ReportInProgress:
            return '5';
        default:
            return '2';
    }
};

/**
 * triggers a download of a file
 * @param fileUrl The url of the file
 * @param fileName the name of the file
 */
export const triggerDownload = (fileUrl: string, fileName: string) => {
    const fileLink = document.createElement('a');
    fileLink.href = fileUrl;
    fileLink.download = fileName;
    fileLink.click();
};

/**
 * Open the specified fileUrl in a new browser tab
 * @param fileUrl the url of the file
 */
export const openInNewTab = (fileUrl: string) => {
    window.open(fileUrl, '_blank');
};

/**
 * Open the specified fileUrl in a new browser tab
 * @param fileUrl the url of the file
 */
export const openPdfPageInNewTab = (fileUrl: string, pageNumber: string) => {
    const url = `${fileUrl}#page=${pageNumber}`;
    window.open(url, '_blank');
};

/**
 * Scrolls the windows to the top
 */
export const handleAlertScroll = () => {
    window.scrollTo(0, 0);
    setTimeout(() => {
        const el: HTMLElement = document.querySelector(('[id^="#notif-"]')) as HTMLElement;
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el?.focus();
    }, 100);
};

export const handleReportFileError = () => {
    handleAlertScroll();
    setDashboardNotification({
        message: `Oops - An unexpected error has occurred with downloading the PDF report. 
            Please wait a minute before reloading this page to try again.`,
        severity: NotificationSeverity.Error,
    });
};

export const handleUnexpectedError = (details: ProblemDetails) => {
    handleAlertScroll();
    setDashboardNotification(DashBoardNotifications.getDashboardErrorNotification(details.status! ?? 0, ''));
};

/**
 * Returns a formatted banner title string
 */
// eslint-disable-next-line arrow-body-style
export const formatBannerTitle = (accountDetails: AccountDetails): string => {
    if (accountDetails !== null && accountDetails.organisation !== undefined) {
        let result = accountDetails.organisation;

        if (accountDetails.trading !== undefined && accountDetails.trading !== '') {
            result = `${result} - ${accountDetails.trading}`;
        }

        if (accountDetails.branch !== undefined && accountDetails.branch !== '') {
            result = `${result} - ${accountDetails.branch}`;
        }

        return result;
    }
    return '';
};

export const getEnvironment = () => {
    let environment = '';
    if (window.location.hostname.includes('localhost')) {
        environment = Environment.Local;
    }
    if (window.location.hostname.includes('.dev.')) {
        environment = Environment.Dev;
    }
    if (window.location.hostname.includes('.test.')) {
        environment = Environment.Test;
    }
    if (window.location.hostname.includes('.preprod.')) {
        environment = Environment.PreProd;
    }
    if (window.location.hostname.includes('.uat.')) {
        environment = Environment.UAT;
    }
    return environment;
};
